import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { fetchShareRateCurrency } from 'actions';
import {
  copyImageToClipboard,
  disableScroll,
  downloadFile,
  enableScroll,
  getSocialShareUrl,
  openExternalShareUrl,
} from 'utils';
import Icon from 'components/commons/Icon';

import CurrencyRateShareActions from 'components/CurrencyRateShareActions';

import {
  Content,
  Footer,
  Header,
  PreviewBox,
  PreviewImage,
  StyledModal,
  Title,
  Wrapper,
} from './styles';

StyledModal.setAppElement('#root');

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99999999,
    backgroundColor: 'rgba(17, 21, 18, 0.32)',
  },
};

const withShareCacheBuster = url => {
  if (!url || typeof window === 'undefined') {
    return url;
  }

  try {
    const parsed = new window.URL(url);
    parsed.searchParams.set('mf_share_ts', String(Date.now()));
    return parsed.toString();
  } catch (error) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}mf_share_ts=${Date.now()}`;
  }
};

const CurrencyRateShareModal = ({ onClose, isModalOpen, data }) => {
  const [shareAsset, setShareAsset] = useState(null);
  const [imageRatio, setImageRatio] = useState(0.943);
  const [isPending, setIsPending] = useState(false);
  const [isAssetLoading, setIsAssetLoading] = useState(true);
  const [isAssetError, setIsAssetError] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const currencyCode = useMemo(() => (data?.code || 'USD').toUpperCase(), [
    data?.code,
  ]);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      setIsAssetLoading(true);
      setIsAssetError(false);
      const response = await fetchShareRateCurrency();
      const imageUrl = response?.url || '';
      let blob = null;
      let file = null;
      let hasError = false;

      if (!imageUrl) {
        hasError = true;
      }

      if (!hasError && imageUrl && typeof window !== 'undefined') {
        try {
          const imageResponse = await fetch(imageUrl);
          if (!imageResponse.ok) {
            throw new Error('Failed to load share image');
          }
          blob = await imageResponse.blob();
          file = new File(
            [blob],
            `currency-${currencyCode.toLowerCase()}.png`,
            {
              type: blob.type || 'image/png',
            }
          );

          const objectUrl = window.URL.createObjectURL(blob);
          const ratio = await new Promise(resolve => {
            const img = new window.Image();

            img.onload = () => {
              const nextRatio =
                img.naturalWidth && img.naturalHeight
                  ? img.naturalWidth / img.naturalHeight
                  : 0.943;
              resolve(nextRatio);
            };
            img.onerror = () => resolve(0.943);
            img.src = objectUrl;
          });

          window.URL.revokeObjectURL(objectUrl);
          setImageRatio(ratio);
        } catch (error) {
          blob = null;
          file = null;
          hasError = true;
        }
      }

      const asset = {
        file,
        blob,
        url: imageUrl,
        filename: file?.name || `currency-${currencyCode.toLowerCase()}.png`,
      };

      if (isMounted) {
        setShareAsset(asset);
        setIsAssetError(hasError);
        setIsAssetLoading(false);
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [currencyCode]);

  useEffect(() => {
    if (
      (!shareAsset?.blob && !shareAsset?.url) ||
      typeof window === 'undefined'
    ) {
      setPreviewUrl('');
      return undefined;
    }

    if (!shareAsset?.blob) {
      setPreviewUrl(shareAsset.url);
      return undefined;
    }

    const objectUrl = window.URL.createObjectURL(shareAsset.blob);
    setPreviewUrl(objectUrl);

    return () => {
      window.URL.revokeObjectURL(objectUrl);
    };
  }, [shareAsset]);

  const handleAction = async actionId => {
    if (!shareAsset || isPending || isAssetLoading || isAssetError) {
      return;
    }

    const { blob, filename } = shareAsset;
    const socialTargetUrl =
      typeof window !== 'undefined'
        ? withShareCacheBuster(window.location.href)
        : '';
    const shareText = 'Курс валют в Україні на Minfin.com.ua';
    setIsPending(true);

    try {
      if (['telegram', 'viber', 'facebook', 'x'].includes(actionId)) {
        const shareUrl = getSocialShareUrl({
          actionId,
          pageUrl: socialTargetUrl,
          shareText,
        });
        openExternalShareUrl(shareUrl, actionId);
        return;
      }

      if (actionId === 'copy') {
        if (blob) {
          await copyImageToClipboard(blob).catch(() => false);
        }
        return;
      }

      if (actionId === 'download') {
        if (blob) {
          downloadFile({ blob, filename });
        }
        return;
      }
    } finally {
      setIsPending(false);
    }
  };

  return (
    <StyledModal
      $imageRatio={imageRatio}
      isOpen={isModalOpen}
      onRequestClose={onClose}
      onAfterOpen={disableScroll}
      onAfterClose={enableScroll}
      shouldCloseOnOverlayClick
      style={modalStyles}
    >
      <Wrapper>
        <Header>
          <Title>
            <FormattedMessage id="share" />
          </Title>
          <button type="button" onClick={onClose}>
            <Icon name="close" />
          </button>
        </Header>
        <Content>
          {isAssetLoading && (
            <PreviewBox $ratio={imageRatio}>
              <div>
                <FormattedMessage id="share.modal.loadingImage" />
              </div>
            </PreviewBox>
          )}
          {!isAssetLoading && isAssetError && (
            <PreviewBox $ratio={imageRatio}>
              <div>Помилка завантаження</div>
            </PreviewBox>
          )}
          {!isAssetLoading && !isAssetError && shareAsset && (
            <PreviewImage
              $ratio={imageRatio}
              src={previewUrl}
              alt={`currency-${currencyCode}`}
            />
          )}
        </Content>
        <Footer>
          <CurrencyRateShareActions
            onAction={handleAction}
            disabled={isAssetLoading || isPending || isAssetError}
            hasBlob={Boolean(shareAsset?.blob)}
          />
        </Footer>
      </Wrapper>
    </StyledModal>
  );
};

CurrencyRateShareModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.string),
};

CurrencyRateShareModal.defaultProps = {
  data: null,
};

export default CurrencyRateShareModal;
